import { LoggedOutGeneratorSection } from '../../LoggedOutCodeGenerator.types'
import { MutableRefObject, RefObject, useCallback, useEffect, useRef } from 'react'
import {
  selectFirstSectionIsActive,
  selectSecondSectionIsActive
} from '@app/code/src/machines/locg/locgSelectors'
import { useGlobalServices } from '@app/common/src/hooks/useGlobalServices'
import { useScreenSizeBreakpoints } from '@app/common/src/hooks/useScreenSizeBreakpoints'
import { useSelector } from '@xstate/react'
import { useTheme } from '@mui/material'

interface ScrollableContainerState {
  rootContainerRef: RefObject<HTMLDivElement>
  scanDestinationRef: RefObject<HTMLDivElement>
  codeDesignContainerRef: RefObject<HTMLDivElement>
  codeDesignRefs: MutableRefObject<HTMLDivElement[]>
  authRef: RefObject<HTMLDivElement>
}

export const useScrollableContainerState = (): ScrollableContainerState => {
  const theme = useTheme()
  const { isMobile } = useScreenSizeBreakpoints()
  const rootContainerRef = useRef<HTMLDivElement>(null)
  const scanDestinationRef = useRef<HTMLDivElement>(null)
  const codeDesignContainerRef = useRef<HTMLDivElement>(null)
  const codeDesignRefs = useRef<HTMLDivElement[]>([])
  const authRef = useRef<HTMLDivElement>(null)
  const observerRef = useRef<IntersectionObserver | null>(null)

  const { locgService } = useGlobalServices()
  const codeDesignSectionIsActive = useSelector(locgService, state => state.matches('codeDesign'))
  const firstSectionIsActive = useSelector(locgService, selectFirstSectionIsActive)
  const secondSectionIsActive = useSelector(locgService, selectSecondSectionIsActive)

  /**
   * Add keyboard event handlers to progress through the form when <enter> is pressed
   */
  useEffect(() => {
    const rootContainer = rootContainerRef.current
    const createKeyDownHandler =
      (cb: () => void) =>
      (e: KeyboardEvent): void => {
        if (
          e.key === 'Enter' &&
          e.target &&
          'nodeName' in e.target &&
          e.target.nodeName !== 'BUTTON'
        ) {
          cb()
        }
      }

    if (firstSectionIsActive) {
      const handleKeyDown = createKeyDownHandler(() => {
        return locgService.send({ type: LoggedOutGeneratorSection.CodeDesign })
      })
      rootContainer?.addEventListener('keydown', handleKeyDown)
      return () => {
        rootContainer?.removeEventListener('keydown', handleKeyDown)
      }
    }

    if (secondSectionIsActive) {
      const handleKeyDown = createKeyDownHandler(() => {
        return locgService.send({
          type: 'DOWNLOAD_CODE',
          target: LoggedOutGeneratorSection.SignUp
        })
      })
      rootContainer?.addEventListener('keydown', handleKeyDown)
      return () => {
        rootContainer?.removeEventListener('keydown', handleKeyDown)
      }
    }
  }, [firstSectionIsActive, locgService, secondSectionIsActive])

  /**
   * When a new section comes into view, send state transition event
   */
  const observerCallback = useCallback(
    ([entry]: IntersectionObserverEntry[]): void => {
      if (entry.isIntersecting) {
        locgService.send({
          type: entry.target.id as LoggedOutGeneratorSection
        })
      }
    },
    [locgService]
  )

  useEffect(() => {
    const threshold = isMobile ? [0.25, 0.5, 1] : 0.7

    if (codeDesignRefs.current.length > 0 && codeDesignSectionIsActive) {
      if (!observerRef.current) {
        observerRef.current = new IntersectionObserver(observerCallback, {
          root: rootContainerRef.current,
          threshold,
          rootMargin: `0px 0px ${isMobile ? 0 : -170}px 0px`
        })
      }

      // observe code design sections
      codeDesignRefs.current.forEach(element => {
        observerRef.current?.observe(element)
      })
    }

    return () => {
      observerRef.current?.disconnect()
    }
  }, [isMobile, locgService, observerCallback, theme, codeDesignSectionIsActive])

  return {
    rootContainerRef,
    scanDestinationRef,
    codeDesignContainerRef,
    codeDesignRefs,
    authRef
  }
}
